import { skipToken, useQueryClient } from "@tanstack/react-query";

import { useFetchMutation, useFetchQuery } from "common/queries/hooks";
import { noop } from "common/utils/noop";

import { useClientUserQuery } from "client/accounts/hooks";
import { useClientPrimaryContactPreferencesQuery } from "client/inbox/hooks";

import {
  DeliveryMethod,
  DosageReminder,
  DosageReminderCreate,
  DosageReminderPatientItem,
  DosageReminderPatientItemSearch,
  DosageRemindersSearch,
  DosageReminderUpdate,
} from "../types";
import { DELIVERY_METHOD_OPTIONS } from "../constants";
import { getChipVariantByPatientId } from "../utils";
import { keys, urls } from "./constants";

const excludeDeletedReminders = (reminders: DosageReminder[]) =>
  reminders.filter((reminder) => !reminder.is_deleted);

export const useDosageRemindersQuery = (
  search: DosageRemindersSearch = {},
  { isEnabled = true }: { isEnabled?: boolean } = {},
) => {
  return useFetchQuery<DosageReminder[], DosageRemindersSearch>(keys.dosageReminderList(search), {
    fetch: isEnabled ? { url: urls.dosageReminders(), body: search } : skipToken,
    config: { select: excludeDeletedReminders },
  });
};

// Shares the cache with useDosageRemindersQuery. Deleted reminders still count toward patient chip colors
const useAllDosageRemindersQuery = () => {
  return useFetchQuery<DosageReminder[], DosageRemindersSearch>(keys.dosageReminderList({}), {
    fetch: { url: urls.dosageReminders(), body: {} },
  });
};

export const usePatientChipVariants = (otherPatientIds?: number[]) => {
  const { data: reminders } = useAllDosageRemindersQuery();
  return getChipVariantByPatientId({ reminders: reminders ?? [], otherPatientIds });
};

export const useDosageReminderQuery = (id: number) => {
  return useFetchQuery<DosageReminder>(keys.dosageReminderDetail(id), {
    fetch: { url: urls.dosageReminder(id) },
  });
};

export const useDosageReminderRecentItemsQuery = ({
  isEnabled = true,
}: { isEnabled?: boolean } = {}) => {
  return useFetchQuery<DosageReminderPatientItem[]>(keys.dosageReminderRecentItems(), {
    fetch: isEnabled ? { url: urls.dosageReminderRecentItems() } : skipToken,
  });
};

export const useDosageReminderPatientItemQuery = (search: DosageReminderPatientItemSearch) => {
  return useFetchQuery<DosageReminderPatientItem, DosageReminderPatientItemSearch>(
    keys.dosageReminderPatientItem(search),
    { fetch: { url: urls.dosageReminderPatientItem(), body: search } },
  );
};

export const useCreateDosageReminderMutation = ({
  onSuccess = noop,
}: { onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient();
  return useFetchMutation<DosageReminderCreate, Pick<DosageReminder, "id">>({
    fetch: (body) => ({ method: "POST", url: urls.dosageReminders(), body }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.dosageReminder() });
        onSuccess();
      },
    },
  });
};

export const useUpdateDosageReminderMutation = (
  id: number,
  { onSuccess = noop }: { onSuccess?: () => void } = {},
) => {
  const queryClient = useQueryClient();
  return useFetchMutation<DosageReminderUpdate, DosageReminder>({
    fetch: (body) => ({ method: "PATCH", url: urls.dosageReminder(id), body }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.dosageReminder() });
        onSuccess();
      },
    },
  });
};

export const useDeleteDosageReminderMutation = (
  id: number,
  { onSuccess = noop }: { onSuccess?: () => void } = {},
) => {
  const queryClient = useQueryClient();
  return useFetchMutation<void, void>({
    fetch: () => ({ method: "DELETE", url: urls.dosageReminder(id) }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.dosageReminder() });
        onSuccess();
      },
    },
  });
};

/** Matches the patient chip color shown in the reminders list and item select drawer */
export const usePatientChipVariant = (patientId: number) => {
  const { data: reminders } = useAllDosageRemindersQuery();
  // A patient with a reminder is slotted before any otherPatientIds, so recent items can't change its color
  const hasReminder = !!reminders?.some((reminder) => reminder.patient.id === patientId);
  const { data: patientItems } = useDosageReminderRecentItemsQuery({ isEnabled: !hasReminder });

  return getChipVariantByPatientId({
    reminders: reminders ?? [],
    otherPatientIds: patientItems?.map(({ patient }) => patient.id),
  }).get(patientId);
};

export const useDeliveryMethodSelection = (value: Record<DeliveryMethod, boolean>) => {
  const { data: clientUser, isLoading } = useClientUserQuery();
  const { data: contactPreferences, isLoading: isLoadingContactPreferences } =
    useClientPrimaryContactPreferencesQuery();

  const destinations: Record<DeliveryMethod, string | undefined> = {
    text: clientUser?.primary_contact_number?.number_display,
    email: clientUser?.primary_contact_email?.email,
  };
  const isTextUnsubscribed = contactPreferences?.primary_contact_number?.is_subscribed === false;
  const isTextHardUnsubscribed = !!contactPreferences?.primary_contact_number?.is_hard_unsubscribed;

  const availableMethods: Record<DeliveryMethod, boolean> = {
    text: !!destinations.text && !isTextUnsubscribed,
    email: !!destinations.email,
  };
  const availableMethodCount = DELIVERY_METHOD_OPTIONS.filter(
    (option) => availableMethods[option.value],
  ).length;

  // An unavailable method can't be selected, and a lone available method can't be deselected
  const isSelected = (method: DeliveryMethod) =>
    availableMethods[method] && (availableMethodCount === 1 || value[method]);

  return {
    clientUser,
    destinations,
    availableMethods,
    isTextUnsubscribed,
    isTextHardUnsubscribed,
    availableMethodCount,
    isSelected,
    isLoading: isLoading || isLoadingContactPreferences,
  };
};
